import { copy } from '@ohear/shared'
import { Badge, Button, Card, Spinner, useToast } from '@ohear/ui'
import { Navigate, createFileRoute } from '@tanstack/react-router'
import { AccountTabs } from '../../features/account/AccountTabs'
import { useAccountProfile } from '../../lib/api/account'
import { useStripeConnectStatus, useStripeOnboardingLink } from '../../lib/api/earnings'
import type { StripeAccountStatus } from '../../lib/api/earnings'
import { useT } from '../../lib/i18n/use-t'
import { AccountHeader } from './profile'

export const Route = createFileRoute('/account/payment')({
  component: PaymentPage,
})

function PaymentPage() {
  const t = useT()
  const profile = useAccountProfile()
  const status = useStripeConnectStatus()
  const onboard = useStripeOnboardingLink()
  const toast = useToast()

  if (profile.isLoading) {
    return (
      <Section>
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading...
        </div>
      </Section>
    )
  }
  if (!profile.data) {
    return (
      <Section>
        <Card>
          <p className="m-0">{t(copy.common.SIGN_IN_REQUIRED.payment)}</p>
        </Card>
      </Section>
    )
  }
  if (profile.data.primary_role !== 'actor') {
    return <Navigate to="/account/profile" replace />
  }

  const openStripe = () => {
    onboard.mutate(undefined, {
      onSuccess: (data) => {
        // AccountLinks are single-use; same-tab nav lets the user come back via
        // Stripe's "return to OHEAR" button.
        window.location.assign(data.url)
      },
      onError: (err) => {
        toast.show({
          severity: 'error',
          title: 'Could not open Stripe',
          description: err instanceof Error ? err.message : 'Unknown error',
        })
      },
    })
  }

  return (
    <Section profile={profile.data}>
      <AccountTabs active="payment" isActor={true}>
        <div className="flex flex-col gap-4">
          <Card title="Stripe Express">
            {status.isLoading ? (
              <div className="flex items-center gap-2 text-[var(--text-2)]">
                <Spinner /> Loading account status...
              </div>
            ) : status.error || !status.data ? (
              <p className="text-[13px] text-[var(--red)] m-0">
                {status.error instanceof Error ? status.error.message : 'Status unavailable.'}
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-medium">Status</span>
                  <StatusBadge status={status.data.status} />
                  {status.data.payouts_enabled ? (
                    <Badge variant="green">payouts enabled</Badge>
                  ) : (
                    <Badge variant="amber">payouts disabled</Badge>
                  )}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] m-0">
                  <Detail
                    label="Bank account"
                    value={
                      status.data.bank_last4
                        ? `**** **** **** ${status.data.bank_last4}`
                        : 'Not on file'
                    }
                  />
                  <Detail
                    label="Last payout"
                    value={
                      status.data.last_payout_at
                        ? new Date(status.data.last_payout_at).toLocaleDateString()
                        : 'Never'
                    }
                  />
                </dl>
                <div>
                  <Button variant="primary" loading={onboard.isPending} onClick={openStripe}>
                    {status.data.status === 'active'
                      ? 'Manage in Stripe Express'
                      : 'Set up Stripe Express'}
                  </Button>
                </div>
                <p className="text-[12px] text-[var(--text-2)] m-0">
                  OHEAR.AI doesn't store your banking information. All KYC and payouts are handled
                  directly by Stripe.
                </p>
              </div>
            )}
          </Card>
        </div>
      </AccountTabs>
    </Section>
  )
}

function StatusBadge({ status }: { status: StripeAccountStatus }) {
  switch (status) {
    case 'active':
      return <Badge variant="green">active</Badge>
    case 'pending':
      return <Badge variant="amber">pending</Badge>
    case 'restricted':
      return <Badge variant="amber">restricted</Badge>
    case 'rejected':
      return <Badge variant="red">rejected</Badge>
    case 'not_started':
    default:
      return <Badge variant="gray">not started</Badge>
  }
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[12px] text-[var(--text-2)] m-0">{label}</dt>
      <dd className="m-0 mt-0.5 font-medium tabular-nums">{value}</dd>
    </div>
  )
}

function Section({
  children,
  profile,
}: {
  children: React.ReactNode
  profile?: import('../../lib/api/account').AccountProfile
}) {
  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile} />
      {children}
    </section>
  )
}
